import React, { useLayoutEffect, useRef, useState } from "react";
import {
  format,
  addDays,
  differenceInCalendarDays,
  parseISO,
  isBefore,
  getDaysInMonth,
  addMonths,
} from "date-fns";
import { Button, Grid, Stack, Typography, Tooltip } from "@mui/material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useTranslation } from "react-i18next";
import { useResizeObserver } from "../../common/resizerObserver";
import { formatDate, isWeekendDay } from "../../common/DateUtil";
import { Link } from "react-router-dom";

const ROW_HEIGHT = 40;
const BAR_HEIGHT = 26;
const BOOKING_SPACING = 2;

const BookingCalendar = ({
  stayResources,
  reservationPeriods,
  startDate,
  setStartDate,
}) => {
  const { t } = useTranslation();
  const tableWrapRef = useRef(null);
  const tableRef = useRef(null);
  const theadRef = useRef(null);
  const firstColRef = useRef(null);

  const [firstColWidth, setFirstColWidth] = useState(0);
  const [dayWidth, setDayWidth] = useState(0);

  const daysInMonth = getDaysInMonth(startDate);
  const days = Array.from({ length: daysInMonth }, (_, i) =>
    addDays(startDate, i),
  );
  const headerHeight = theadRef.current?.offsetHeight ?? 0;

  useLayoutEffect(() => {
    if (firstColRef.current && tableRef.current) {
      const firstCol = firstColRef.current.offsetWidth;
      const totalWidth = tableRef.current.clientWidth;
      const availableWidth = totalWidth - firstCol;

      setFirstColWidth(firstCol);
      setDayWidth(availableWidth / daysInMonth);
    }
  }, [startDate, daysInMonth]);

  useResizeObserver(tableRef, () => {
    if (firstColRef.current && tableRef.current) {
      const firstCol = firstColRef.current.offsetWidth;
      const totalWidth = tableRef.current.clientWidth;
      const availableWidth = totalWidth - firstCol;

      setFirstColWidth(firstCol);
      setDayWidth(availableWidth / daysInMonth);
    }
  });

  const getBookingsForResource = (resourceId) =>
    reservationPeriods.filter((event) => event.stay.id === resourceId);

  const handleMonthRefresh = (days) => {
    setStartDate(addMonths(startDate, days));
  };

  const month = startDate.toLocaleString("en", { month: "long" }).toLowerCase();

  return (
    <>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<KeyboardDoubleArrowLeftIcon />}
            color="primary"
            onClick={() => handleMonthRefresh(-1)}
            data-testid="prev-month-button"
          />
          <Button variant="outlined" data-testid="current-month-display">{t(month)}</Button>
          <Button
            startIcon={<KeyboardDoubleArrowRightIcon />}
            variant="contained"
            color="primary"
            onClick={() => handleMonthRefresh(1)}
            data-testid="next-month-button"
          />
        </Stack>
      </Grid>

      {/* Relative wrapper zodat de overlay erboven kan liggen */}
      <div
        ref={tableWrapRef}
        style={{
          position: "relative",
          overflowX: "hidden",
          backgroundColor: "white",
        }}
      >
        <table
          ref={tableRef}
          style={{
            borderCollapse: "collapse",
            minWidth: "1200px",
            width: "100%",
            tableLayout: "fixed",
          }}
        >
          <thead ref={theadRef}>
            <tr>
              <th
                ref={firstColRef}
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  width: "8em",
                }}
              >
                {t("stay")}
              </th>
              {days.map((day) => (
                <th
                  key={day.toISOString()}
                  style={{
                    border: "1px solid #ccc",
                    padding: "8px",
                    width: `${100 / daysInMonth}%`,
                    backgroundColor: isWeekendDay(day) ? "#F3F3F3FF" : "",
                  }}
                >
                  {format(day, "d")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stayResources.map((resource) => (
              <tr key={resource.id} style={{ height: ROW_HEIGHT }}>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 8,
                    fontWeight: "bold",
                    background: "#f9f9f9",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {resource.name}
                </td>
                {days.map((day) => (
                  <td
                    key={resource.id + "-" + format(day, "yyyy-MM-dd")}
                    style={{
                      border: "1px solid #ccc",
                      padding: 0,
                      position: "relative",
                      height: ROW_HEIGHT,
                      backgroundColor: isWeekendDay(day) ? "#f3f3f3" : "",
                    }}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Overlay boven de hele tabel: balken + hover werken overal */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            pointerEvents: "none", // events alleen op de balken
          }}
        >
          {stayResources.map((resource, rIdx) => {
            const rowCenterY =
              headerHeight + rIdx * ROW_HEIGHT + ROW_HEIGHT / 2;
            const bookings = getBookingsForResource(resource.id);

            return bookings.map((booking) => {
              const parsedCheckInDate = parseISO(booking.checkIn);
              const checkInBeforeStartDate = isBefore(
                parsedCheckInDate,
                startDate,
              );
              const start = checkInBeforeStartDate
                ? startDate
                : parsedCheckInDate;
              const end = addDays(parseISO(booking.checkOut), 1); // checkOut exclusief
              const startIndex = Math.max(
                0,
                differenceInCalendarDays(start, startDate),
              );
              const spanDays = Math.min(
                daysInMonth - startIndex,
                differenceInCalendarDays(end, start),
              );
              if (startIndex >= daysInMonth || spanDays <= 0) return null;

              // halve dag offset voor nette aansluitingen
              const fractionalStart = checkInBeforeStartDate ? 0 : 0.5;
              const adjustedStart = startIndex + fractionalStart;
              const adjustedEndDate = checkInBeforeStartDate ? 0.5 : 0;

              const left = Math.round(
                firstColWidth + adjustedStart * dayWidth + BOOKING_SPACING,
              );
              const width = Math.round(
                (spanDays + adjustedEndDate) * dayWidth - BOOKING_SPACING,
              );

              return (
                <Tooltip
                  key={booking.id}
                  title={
                    <div style={{ padding: 8 }}>
                      <Typography
                        color="inherit"
                        variant="subtitle2"
                        sx={{ fontWeight: 600, lineHeight: 1.25 }}
                      >
                        <Link
                          to={`/beheer/dieren/${booking.reservation.animals[0].uuid}`}
                          style={{
                            color: "inherit",
                            textDecoration: "underline",
                          }}
                          data-testid={`calendar-booking-link-${booking.reservation.animals[0].name}`}
                        >
                          {t("reservationFor")}{" "}
                          {booking.reservation.animals
                            .map((v) => v.name)
                            .join(" & ")}
                        </Link>
                      </Typography>
                      <div
                        style={{
                          borderTop: "1px solid #eceff1",
                          margin: "6px 0",
                        }}
                      />
                      <Typography color="inherit" variant="body2">
                        <strong>{t("check_in")}</strong>{" "}
                        {formatDate(booking.reservation.fromDate)} -{" "}
                        {booking.reservation.checkInHour}h
                      </Typography>
                      <Typography color="inherit" variant="body2">
                        <strong>{t("check_out")}</strong>{" "}
                        {formatDate(booking.reservation.toDate)} -{" "}
                        {booking.reservation.checkOutHour}h
                      </Typography>
                    </div>
                  }
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "#fff",
                        color: "rgba(0,0,0,0.87)",
                        border: "1px solid #dadde9",
                        boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
                        borderRadius: 1,
                        maxWidth: 420,
                        fontSize: 12,
                        p: 0,
                      },
                    },
                    popper: {
                      modifiers: [
                        { name: "offset", options: { offset: [0, -15] } },
                      ],
                    },
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left,
                      width,
                      height: BAR_HEIGHT,
                      top: rowCenterY,
                      transform: "translateY(-50%)",
                      backgroundColor: "#00acc1",
                      color: "white",
                      padding: "4px 6px",
                      fontSize: "0.75rem",
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box",
                      zIndex: 3,
                      pointerEvents: "auto", // hover/tooltip actief
                      willChange: "transform",
                    }}
                    data-testid={`calendar-booking-bar-${booking.reservation.animals[0].name}`}
                  >
                    {booking.reservation.animals.map((c) => c.name).join(" & ")}
                  </div>
                </Tooltip>
              );
            });
          })}
        </div>
      </div>
    </>
  );
};

export default BookingCalendar;
